"use client";

import React, { useState, useEffect } from 'react';
import { MdClose, MdCheckCircle, MdError, MdWarning, MdInfo, MdNotifications, MdPersonAdd, MdAssignment, MdCancel } from 'react-icons/md';

let notificationId = 0;

const WebNotifications = () => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = (message, type = 'info', duration = 10000) => {
    const id = ++notificationId;
    const notification = { id, message, type, timestamp: Date.now() };
    
    setNotifications(prev => [...prev, notification]);
    
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, duration);
  };

  useEffect(() => {
    window.showWebNotification = addNotification;
    
    // Also listen for custom events as fallback
    const handleWebNotification = (event) => {
      const { message, type, duration } = event.detail;
      addNotification(message, type, duration);
    };
    
    window.addEventListener('webNotification', handleWebNotification);
    
    return () => {
      delete window.showWebNotification;
      window.removeEventListener('webNotification', handleWebNotification);
    };
  }, []);

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const getIcon = (type) => {
    switch (type) {
      case 'success': return <MdCheckCircle className="w-6 h-6 text-green-600" />;
      case 'error': return <MdError className="w-6 h-6 text-red-600" />;
      case 'warning': return <MdWarning className="w-6 h-6 text-yellow-600" />;
      case 'doctor_request': return <MdPersonAdd className="w-6 h-6 text-blue-600" />;
      case 'donor_allotted': return <MdAssignment className="w-6 h-6 text-green-600" />;
      case 'donor_rejected': return <MdCancel className="w-6 h-6 text-red-600" />;
      case 'document_uploaded': return <MdInfo className="w-6 h-6 text-purple-600" />;
      case 'process_completed': return <MdCheckCircle className="w-6 h-6 text-green-600" />;
      default: return <MdInfo className="w-6 h-6 text-blue-600" />;
    }
  };

  const getTitle = (type) => {
    switch (type) {
      case 'success': return 'Success';
      case 'error': return 'Error';
      case 'warning': return 'Warning';
      case 'doctor_request': return 'New Request';
      case 'donor_allotted': return 'Donor Allotted';
      case 'donor_rejected': return 'Request Rejected';
      case 'document_uploaded': return 'Document Uploaded';
      case 'process_completed': return 'Process Completed';
      default: return 'Information';
    }
  };

  const getBorderColor = (type) => {
    switch (type) {
      case 'success': return 'border-l-green-500';
      case 'error': return 'border-l-red-500';
      case 'warning': return 'border-l-yellow-500';
      case 'doctor_request': return 'border-l-blue-500';
      case 'donor_allotted': return 'border-l-green-500';
      case 'donor_rejected': return 'border-l-red-500';
      case 'document_uploaded': return 'border-l-purple-500';
      case 'process_completed': return 'border-l-green-500';
      default: return 'border-l-blue-500';
    }
  };

  if (notifications.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[999999] space-y-3 pointer-events-none max-w-sm">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={`bg-white rounded-lg shadow-xl border-l-4 ${getBorderColor(notification.type)} border-r border-t border-b border-gray-200 p-4 pointer-events-auto transform transition-all duration-300 animate-in slide-in-from-right`}
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              {getIcon(notification.type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-semibold text-gray-900">
                  {getTitle(notification.type)}
                </h4>
                <button
                  onClick={() => removeNotification(notification.id)}
                  className="text-gray-400 hover:text-gray-600 ml-2 transition-colors"
                >
                  <MdClose className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                {notification.message}
              </p>
              <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                <MdNotifications className="w-3 h-3" />
                <span>Just now</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default WebNotifications;
